// src/pages/analysis/AnalysisLogin.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import {
  auth,
  db,
} from "../../utils/firebase";
import "./AnalysisLogin.css";
import bgLogin from "../../images-login/nen10.webp";
import paymentQr from "../../images-login/usdt-bsc-bep20-payment-qr-512.png";
import {
  bootstrapSelfRegistrationTrial,
  shouldBootstrapSelfRegistration,
} from "./analysisSelfRegistrationClient";
import AnalysisSelfRegistrationForm from "./AnalysisSelfRegistrationForm";

const LANG_KEY = "analysis_lang";
const DEFAULT_LANG = "en-GB";

// USDT (BEP20) payment destination shown on the Pro/Premium checkout screen.
// Keep this address on a single line, unabridged — never wrap or truncate it.
const PAYMENT_USDT_BSC_ADDRESS =
  "0x9ed76995077ac8abd955d4e72572ebaad982a43f";
const PAYMENT_SUPPORT_URL = "https://t.me/tickter";

const ANALYSIS_DEVICE_ID_KEY = "analysis_device_id";
const ANALYSIS_SESSION_ID_KEY = "analysis_session_id";
const ANALYSIS_LAST_ACTIVITY_AT_KEY = "analysis_last_activity_at";
const ANALYSIS_SESSION_KICKED_KEY = "analysis_session_kicked_reason";

const LANGUAGE_OPTIONS = [
  {
    code: "ja-JP",
    label: "Japanese",
    flag: "🇯🇵",
    flagUrl: "https://flagcdn.com/w40/jp.png"
  },
  {
    code: "it-IT",
    label: "Italian",
    flag: "🇮🇹",
    flagUrl: "https://flagcdn.com/w40/it.png"
  },
  {
    code: "es-ES",
    label: "Spanish",
    flag: "🇪🇸",
    flagUrl: "https://flagcdn.com/w40/es.png"
  },
  {
    code: "ru-RU",
    label: "Russian",
    flag: "🇷🇺",
    flagUrl: "https://flagcdn.com/w40/ru.png"
  },
  {
    code: "fr-FR",
    label: "French",
    flag: "🇫🇷",
    flagUrl: "https://flagcdn.com/w40/fr.png"
  },
  {
    code: "de-DE",
    label: "German",
    flag: "🇩🇪",
    flagUrl: "https://flagcdn.com/w40/de.png"
  },
  {
    code: "en-GB",
    label: "English",
    flag: "🇬🇧",
    flagUrl: "https://flagcdn.com/w40/gb.png"
  },
  {
    code: "tr-TR",
    label: "Turkish",
    flag: "🇹🇷",
    flagUrl: "https://flagcdn.com/w40/tr.png"
  },
  {
    code: "zh-CN",
    label: "Chinese",
    flag: "🇨🇳",
    flagUrl: "https://flagcdn.com/w40/cn.png"
  },
  {
    code: "th-TH",
    label: "Thai",
    flag: "🇹🇭",
    flagUrl: "https://flagcdn.com/w40/th.png"
  },
  {
    code: "vi-VN",
    label: "Vietnamese",
    flag: "🇻🇳",
    flagUrl: "https://flagcdn.com/w40/vn.png"
  },
  {
    code: "ko-KR",
    label: "Korean",
    flag: "🇰🇷",
    flagUrl: "https://flagcdn.com/w40/kr.png"
  },
  {
    code: "en-SG",
    label: "Singapore",
    flag: "🇸🇬",
    flagUrl: "https://flagcdn.com/w40/sg.png"
  },
  {
    code: "id-ID",
    label: "Indonesian",
    flag: "🇮🇩",
    flagUrl: "https://flagcdn.com/w40/id.png"
  },
  {
    code: "nl-NL",
    label: "Dutch",
    flag: "🇳🇱",
    flagUrl: "https://flagcdn.com/w40/nl.png"
  }
];

function FlagIcon({ item, size = 16 }) {
  const [imgError, setImgError] = useState(false);

  if (!imgError && item.flagUrl) {
    return (
      <img
        src={item.flagUrl}
        alt={item.label}
        width={size + 6}
        height={Math.round((size + 6) * 0.72)}
        onError={() => setImgError(true)}
        style={{
          display: "block",
          width: `${size + 6}px`,
          height: `${Math.round((size + 6) * 0.72)}px`,
          objectFit: "cover",
          borderRadius: "2px",
          flexShrink: 0,
          boxShadow: "0 0 0 1px rgba(255,255,255,.08)"
        }}
      />
    );
  }

  return (
    <span
      style={{
        width: `${size + 6}px`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: `${size}px`,
        lineHeight: 1,
        flexShrink: 0
      }}
      aria-hidden="true"
    >
      {item.flag}
    </span>
  );
}

function safeGetLocalStorage(key, fallback = "") {
  if (typeof window === "undefined") return fallback;
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch {
    return fallback;
  }
}

function safeSetLocalStorage(key, value) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function safeRemoveLocalStorage(key) {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch {}
}

// Copies `value` to the clipboard, preferring the async Clipboard API and
// falling back to a hidden textarea + execCommand("copy") for browsers or
// contexts (e.g. non-HTTPS, older WebViews) where Clipboard API is missing
// or rejects. Returns true only when a copy actually succeeded.
async function copyTextToClipboard(value) {
  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {}
  }

  if (typeof document === "undefined" || !document.body) {
    return false;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  textarea.style.left = "-9999px";

  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, value.length);

  let copied = false;

  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    document.body.removeChild(textarea);
  }

  return copied;
}

function getOrCreateAnalysisDeviceId() {
  let id = safeGetLocalStorage(ANALYSIS_DEVICE_ID_KEY, "").trim();
  if (!id) {
    try {
      id =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    } catch {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    }
    safeSetLocalStorage(ANALYSIS_DEVICE_ID_KEY, id);
  }
  return id;
}

function createAnalysisSessionId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {}
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function applyLangToDocument(lang) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("lang", lang);
}

function broadcastLangChange(lang) {
  if (typeof window === "undefined") return;
  try {
    window.__analysisLang = lang;
    window.dispatchEvent(
      new CustomEvent("analysis-language-change", {
        detail: { lang }
      })
    );
  } catch {}
}

function resolveSavedLang(savedLang) {
  const v = typeof savedLang === "string" ? savedLang.trim() : "";
  return LANGUAGE_OPTIONS.some((o) => o.code === v) ? v : DEFAULT_LANG;
}

function getLoginTranslations(langCode) {
  const code = resolveSavedLang(langCode);

  const base = {
    header: {
      language: "Language",
      packages: "Packages",
      features: "Features",
      support: "Support",
      login: "Login"
    },
    common: { close: "Close" },
    packages: {
      registerLine: "Contact Support to Register.",
      basic: {
        description: [
          "Arbitrage access",
          "Realtime data",
          "Compare odds from 19 major bookmakers worldwide"
        ],
        price: "Price: 399 USD"
      },
      pro: {
        description: [
          "Includes Basic",
          "Unlock Value Bets",
          "View betting methods to avoid bookmaker limit-detection systems"
        ],
        price: "Price: 999 USD"
      },
      premium: {
        description: [
          "Includes Basic and Pro",
          "Unlock Steam Move",
          "Priority support",
          "Telegram alerts when opportunities are detected"
        ],
        price: "Price: 1599 USD"
      }
    },
    features: {
      title: "Features",
      items: [
        {
          name: "Arbitrage",
          description:
            "Compare real-time odds across 19 major bookmakers worldwide and identify two-way arbitrage opportunities. By placing both sides at the right prices, users can lock in a defined profit margin when execution conditions are met."
        },
        {
          name: "Value Bets",
          description:
            "Identify bookmakers offering mispriced lines and positive expected value opportunities. Individual bets can still lose, but over a larger sample size, disciplined value betting is designed to generate long-term profit. The average edge per bet typically ranges from 3% to 10%."
        },
        {
          name: "Steam Move",
          description:
            'Detect abnormal odds drops from sharper bookmakers when the line remains unchanged and the market move appears unusually deep. These signals become more meaningful when the price drops without obvious pressure from match flow, injuries, or red cards. In these cases, the market may be showing signs that something is "in the air."'
        }
      ]
    },
    login: {
      title: "Sign in",
      subtitle: "Access your realtime arbitrage, value betting, and steam move dashboard."
    },
    support: {
      title: "Support",
      intro:
        "Need help with your account, plan, payment, or using the platform? Choose a Telegram contact below.",
      telegram: "Telegram",
      email: "Email",
      contactSupport: "Contact Support",
      fallback: "Support details will be available soon.",
      contactHint:
        "Tap a Telegram ID below to open the correct support account or bot.",
      agentLabel: "Telegram support agent",
      agentDescription:
        "Contact our support agent directly for account, plan, payment, or service assistance.",
      botLabel: "TKB Sport Support Bot",
      botDescription:
        "View usage guides, pricing, payment information, and automated support content."
    }
  };

  const map = {
    "ja-JP": {
      header: {
        language: "言語",
        packages: "プラン",
        features: "機能",
        support: "サポート",
        login: "ログイン"
      },
      common: { close: "閉じる" },
      packages: {
        registerLine: "登録はサポートへお問い合わせください。",
        basic: {
          description: [
            "アービトラージにアクセス",
            "リアルタイムデータ",
            "世界19の主要ブックメーカーのオッズを比較"
          ],
          price: "価格：399 USD"
        },
        pro: {
          description: [
            "Basicを含む",
            "Value Betsを利用可能に",
            "ブックメーカーのリミット検知を避けるベット方法を確認"
          ],
          price: "価格：999 USD"
        },
        premium: {
          description: [
            "BasicとProを含む",
            "Steam Moveを利用可能に",
            "優先サポート",
            "機会を検知したらTelegramで通知"
          ],
          price: "価格：1599 USD"
        }
      },
      features: {
        title: "機能",
        items: [
          {
            name: "Arbitrage",
            description:
              "世界19の主要ブックメーカーのリアルタイムオッズを比較し、二方向のアービトラージ機会を特定します。適正な価格で両面を購入すると、実行条件が満たされたときに定められた利益幅を確定できます。"
          },
          {
            name: "Value Bets",
            description:
              "誤ったラインを提示しているブックメーカーと、プラスの期待値を持つ機会を特定します。個々のベットは負けることもありますが、十分なサンプル数では、規律あるバリューベッティングは長期的な利益を目指して設計されています。1ベットあたりの平均エッジは通常3％から10％の範囲です。"
          },
          {
            name: "Steam Move",
            description:
              "ラインが変わらないのに、より鋭いブックメーカーから異常なオッズ下落が起き、市場の動きが異常に深いと見える場合を検出します。試合の流れ・負傷・レッドカードなど明白な圧力なしに価格が下がるとき、これらのシグナルはより意味を持ちます。そのような状況では、市場に「何かが漂っている」兆候が示されている可能性があります。"
          }
        ]
      },
      login: {
        title: "サインイン",
        subtitle: "リアルタイムの裁定取引、バリューベッツ、Steam Moveのダッシュボードへアクセス。"
      },
      support: {
        title: "サポート",
        intro: "お困りですか？サポートチームに直接お問い合わせください。",
        telegram: "Telegram",
        email: "メール",
        contactSupport: "サポートに連絡",
        fallback: "サポートの詳細は近日公開予定です。"
      }
    },
    "it-IT": {
      header: {
        language: "Lingua",
        packages: "Pacchetti",
        features: "Funzionalità",
        support: "Supporto",
        login: "Accesso"
      },
      common: { close: "Chiudi" },
      packages: {
        registerLine: "Contatta l'assistenza per registrarti.",
        basic: {
          description: [
            "Accesso Arbitrage",
            "Dati in tempo reale",
            "Confronta le quote di 19 principali bookmaker nel mondo"
          ],
          price: "Prezzo: 399 USD"
        },
        pro: {
          description: [
            "Include Basic",
            "Sblocca Value Bets",
            "Visualizza metodi di scommessa per evitare i sistemi di rilevamento dei limiti dei bookmaker"
          ],
          price: "Prezzo: 999 USD"
        },
        premium: {
          description: [
            "Include Basic e Pro",
            "Sblocca Steam Move",
            "Supporto prioritario",
            "Avvisi Telegram quando vengono rilevate opportunità"
          ],
          price: "Prezzo: 1599 USD"
        }
      },
      features: {
        title: "Funzionalità",
        items: [
          {
            name: "Arbitrage",
            description:
              "Confronta le quote in tempo reale di 19 bookmaker principali in tutto il mondo e individua opportunità di arbitraggio a due vie. Piazzando entrambe le parti ai prezzi giusti, si può bloccare un margine di profitto definito quando le condizioni di esecuzione sono soddisfatte."
          },
          {
            name: "Value Bets",
            description:
              "Individua bookmaker con linee mal quotate e opportunità a valore atteso positivo. Le singole scommesse possono ancora perdere, ma su un campione più ampio, il value betting disciplinato è pensato per generare profitto nel lungo periodo. Il margine medio per scommessa è in genere tra il 3% e il 10%."
          },
          {
            name: "Steam Move",
            description:
              "Rileva cali anomali delle quote da bookmaker più «sharp» quando la linea resta invariata e il movimento di mercato sembra insolitamente profondo. Questi segnali diventano più significativi quando il prezzo scende senza una pressione evidente da ritmo di partita, infortuni o cartellini rossi. In questi casi il mercato può mostrare segni che «c’è qualcosa nell’aria»."
          }
        ]
      },
      login: {
        title: "Accesso",
        subtitle: "Accedi al tuo dashboard di arbitraggio in tempo reale, value betting e steam move."
      },
      support: {
        title: "Supporto",
        intro: "Serve aiuto? Contatta direttamente il nostro team di supporto.",
        telegram: "Telegram",
        email: "Email",
        contactSupport: "Contatta il supporto",
        fallback: "I dettagli del supporto saranno disponibili a breve."
      }
    },
    "es-ES": {
      header: {
        language: "Idioma",
        packages: "Paquetes",
        features: "Funciones",
        support: "Soporte",
        login: "Iniciar sesión"
      },
      common: { close: "Cerrar" },
      packages: {
        registerLine: "Contacta con soporte para registrarte.",
        basic: {
          description: [
            "Acceso a Arbitrage",
            "Datos en tiempo real",
            "Compara cuotas de 19 casas de apuestas importantes en todo el mundo"
          ],
          price: "Precio: 399 USD"
        },
        pro: {
          description: [
            "Incluye Basic",
            "Desbloquea Value Bets",
            "Consulta métodos de apuesta para evitar sistemas de detección de límites de las casas"
          ],
          price: "Precio: 999 USD"
        },
        premium: {
          description: [
            "Incluye Basic y Pro",
            "Desbloquea Steam Move",
            "Soporte prioritario",
            "Alertas por Telegram cuando se detecten oportunidades"
          ],
          price: "Precio: 1599 USD"
        }
      },
      features: {
        title: "Funciones",
        items: [
          {
            name: "Arbitrage",
            description:
              "Compara cuotas en tiempo real entre 19 casas de apuestas importantes en todo el mundo e identifica oportunidades de arbitraje a dos vías. Al colocar ambos lados a los precios adecuados, puedes asegurar un margen de beneficio definido cuando se cumplen las condiciones de ejecución."
          },
          {
            name: "Value Bets",
            description:
              "Identifica casas con líneas mal cotizadas y oportunidades de valor esperado positivo. Las apuestas individuales pueden perder, pero con una muestra mayor, el value betting disciplinado está pensado para generar beneficio a largo plazo. El margen medio por apuesta suele situarse entre el 3% y el 10%."
          },
          {
            name: "Steam Move",
            description:
              "Detecta caídas anómalas de cuotas en casas más «sharp» cuando la línea no cambia y el movimiento del mercado parece inusualmente profundo. Estas señales cobran más sentido cuando el precio baja sin presión evidente por el ritmo del partido, lesiones o expulsiones. En esos casos, el mercado puede dar señales de que «algo hay en el aire»."
          }
        ]
      },
      login: {
        title: "Iniciar sesión",
        subtitle: "Accede a tu panel de arbitraje en tiempo real, value betting y steam move."
      },
      support: {
        title: "Soporte",
        intro: "¿Necesitas ayuda? Habla directamente con nuestro equipo de soporte.",
        telegram: "Telegram",
        email: "Correo",
        contactSupport: "Contactar soporte",
        fallback: "Los datos de soporte estarán disponibles pronto."
      }
    },
    "ru-RU": {
      header: {
        language: "Язык",
        packages: "Тарифы",
        features: "Возможности",
        support: "Поддержка",
        login: "Вход"
      },
      common: { close: "Закрыть" },
      packages: {
        registerLine: "Свяжитесь с поддержкой для регистрации.",
        basic: {
          description: [
            "Доступ к Arbitrage",
            "Данные в реальном времени",
            "Сравнение коэффициентов у 19 крупных букмекеров по всему миру"
          ],
          price: "Цена: 399 USD"
        },
        pro: {
          description: [
            "Включает Basic",
            "Разблокирует Value Bets",
            "Методы ставок, помогающие обходить системы выявления лимитов букмекеров"
          ],
          price: "Цена: 999 USD"
        },
        premium: {
          description: [
            "Включает Basic и Pro",
            "Разблокирует Steam Move",
            "Приоритетная поддержка",
            "Уведомления в Telegram при обнаружении возможностей"
          ],
          price: "Цена: 1599 USD"
        }
      },
      features: {
        title: "Возможности",
        items: [
          {
            name: "Arbitrage",
            description:
              "Сравнивайте коэффициенты в реальном времени у 19 крупных букмекеров по всему миру и находите двусторонние арбитражные возможности. Размещая обе стороны по правильным ценам, можно зафиксировать заданную прибыль при выполнении условий исполнения."
          },
          {
            name: "Value Bets",
            description:
              "Выявляйте букмекеров с ошибочными линиями и возможности с положительным математическим ожиданием. Отдельные ставки всё ещё могут проигрывать, но на большей выборке дисциплинированный value betting рассчитан на долгосрочную прибыль. Среднее преимущество (edge) на одну ставку обычно составляет от 3% до 10%."
          },
          {
            name: "Steam Move",
            description:
              "Выявляйте аномальное падение коэффициентов у более «острых» букмекеров, когда линия не меняется, а движение рынка кажется необычно глубоким. Такие сигналы важнее, когда цена падает без явного давления со стороны хода матча, травм или удалений. В таких ситуациях рынок может намекать, что «что-то в воздухе»."
          }
        ]
      },
      login: {
        title: "Вход",
        subtitle: "Доступ к вашему дашборду: арбитраж в реальном времени, value betting и steam move."
      },
      support: {
        title: "Поддержка",
        intro: "Нужна помощь? Напишите в службу поддержки напрямую.",
        telegram: "Telegram",
        email: "Email",
        contactSupport: "Связаться с поддержкой",
        fallback: "Контакты поддержки скоро появятся."
      }
    },
    "fr-FR": {
      header: {
        language: "Langue",
        packages: "Forfaits",
        features: "Fonctionnalités",
        support: "Assistance",
        login: "Connexion"
      },
      common: { close: "Fermer" },
      packages: {
        registerLine: "Contactez l'assistance pour vous inscrire.",
        basic: {
          description: [
            "Accès Arbitrage",
            "Données en temps réel",
            "Comparez les cotes de 19 grands bookmakers dans le monde"
          ],
          price: "Prix : 399 USD"
        },
        pro: {
          description: [
            "Inclut Basic",
            "Débloque les Value Bets",
            "Méthodes de paris pour limiter la détection des plafonds par les bookmakers"
          ],
          price: "Prix : 999 USD"
        },
        premium: {
          description: [
            "Inclut Basic et Pro",
            "Débloque Steam Move",
            "Assistance prioritaire",
            "Alertes Telegram lorsque des opportunités sont détectées"
          ],
          price: "Prix : 1599 USD"
        }
      },
      features: {
        title: "Fonctionnalités",
        items: [
          {
            name: "Arbitrage",
            description:
              "Comparez les cotes en temps réel sur 19 grands bookmakers dans le monde et identifiez les opportunités d'arbitrage à deux issues. En plaçant les deux côtés aux bons prix, vous pouvez verrouiller une marge de profit définie lorsque les conditions d'exécution sont réunies."
          },
          {
            name: "Value Bets",
            description:
              "Repérez les bookmakers avec des lignes mal cotées et les opportunités à espérance positive. Les paris individuels peuvent encore perdre, mais sur un échantillon plus large, le value betting discipliné vise un profit à long terme. L'avantage moyen par pari se situe généralement entre 3 % et 10 %."
          },
          {
            name: "Steam Move",
            description:
              "Détectez des baisses de cotes anormales chez des bookmakers plus « sharp » lorsque la ligne ne bouge pas et que le mouvement du marché semble anormalement profond. Ces signaux prennent plus de sens lorsque le prix baisse sans pression évidente du rythme du match, des blessures ou des cartons rouges. Dans ces cas, le marché peut montrer des signes que « quelque chose se prépare »."
          }
        ]
      },
      login: {
        title: "Connexion",
        subtitle: "Accédez à votre tableau de bord d'arbitrage en temps réel, de value betting et de steam move."
      },
      support: {
        title: "Assistance",
        intro: "Besoin d'aide ? Contactez directement notre équipe support.",
        telegram: "Telegram",
        email: "E-mail",
        contactSupport: "Contacter le support",
        fallback: "Les coordonnées du support seront bientôt disponibles."
      }
    },
    "de-DE": {
      header: {
        language: "Sprache",
        packages: "Pakete",
        features: "Funktionen",
        support: "Support",
        login: "Anmelden"
      },
      common: { close: "Schließen" },
      packages: {
        registerLine: "Kontaktieren Sie den Support zur Registrierung.",
        basic: {
          description: [
            "Arbitrage-Zugriff",
            "Echtzeitdaten",
            "Quoten von 19 großen Buchmachern weltweit vergleichen"
          ],
          price: "Preis: 399 USD"
        },
        pro: {
          description: [
            "Beinhaltet Basic",
            "Schaltet Value Bets frei",
            "Wettmethoden zur Vermeidung von Limit-Erkennungssystemen der Buchmacher"
          ],
          price: "Preis: 999 USD"
        },
        premium: {
          description: [
            "Beinhaltet Basic und Pro",
            "Schaltet Steam Move frei",
            "Prioritäts-Support",
            "Telegram-Benachrichtigungen bei erkannten Chancen"
          ],
          price: "Preis: 1599 USD"
        }
      },
      features: {
        title: "Funktionen",
        items: [
          {
            name: "Arbitrage",
            description:
              "Vergleichen Sie Echtzeit-Quoten von 19 großen Buchmachern weltweit und erkennen Sie Zwei-Wege-Arbitrage-Chancen. Werden beide Seiten zu den richtigen Kursen platziert, lässt sich bei erfüllten Ausführungsbedingungen eine definierte Gewinnmarge sichern."
          },
          {
            name: "Value Bets",
            description:
              "Erkennen Sie Buchmacher mit falsch bepreisten Linien und Chancen mit positivem Erwartungswert. Einzelwetten können weiterhin verlieren, aber über eine größere Stichprobe soll diszipliniertes Value Betting langfristigen Gewinn erzielen. Der durchschnittliche Edge pro Wette liegt typischerweise zwischen 3 % und 10 %."
          },
          {
            name: "Steam Move",
            description:
              "Erkennen Sie ungewöhnliche Quotenrückgänge bei schärferen Buchmachern, wenn die Linie gleich bleibt und die Marktbewegung ungewöhnlich tief wirkt. Diese Signale sind aussagekräftiger, wenn der Kurs ohne offensichtlichen Druck durch Spielverlauf, Verletzungen oder Rote Karten fällt. Dann kann der Markt Anzeichen dafür zeigen, dass «etwas in der Luft liegt»."
          }
        ]
      },
      login: {
        title: "Anmelden",
        subtitle: "Zugriff auf dein Dashboard für Echtzeit-Arbitrage, Value Betting und Steam Move."
      },
      support: {
        title: "Support",
        intro: "Brauchst du Hilfe? Sprich direkt mit unserem Support-Team.",
        telegram: "Telegram",
        email: "E-Mail",
        contactSupport: "Support kontaktieren",
        fallback: "Support-Details folgen in Kürze."
      }
    },
    "en-GB": base,
    "tr-TR": {
      header: {
        language: "Dil",
        packages: "Paketler",
        features: "Özellikler",
        support: "Destek",
        login: "Giriş"
      },
      common: { close: "Kapat" },
      packages: {
        registerLine: "Kayıt için destekle iletişime geçin.",
        basic: {
          description: [
            "Arbitrage erişimi",
            "Gerçek zamanlı veri",
            "Dünya çapında 19 büyük bahis sitesinin oranlarını karşılaştırın"
          ],
          price: "Fiyat: 399 USD"
        },
        pro: {
          description: [
            "Basic'i içerir",
            "Value Bets'i açar",
            "Bahisçi limit tespit sistemlerinden kaçınmak için yöntemleri görüntüleyin"
          ],
          price: "Fiyat: 999 USD"
        },
        premium: {
          description: [
            "Basic ve Pro'yu içerir",
            "Steam Move'u açar",
            "Öncelikli destek",
            "Fırsatlar algılandığında Telegram uyarıları"
          ],
          price: "Fiyat: 1599 USD"
        }
      },
      features: {
        title: "Özellikler",
        items: [
          {
            name: "Arbitrage",
            description:
              "Dünya çapında 19 büyük bahis sitesinin gerçek zamanlı oranlarını karşılaştırın ve iki yönlü arbitraj fırsatlarını belirleyin. Her iki tarafı doğru fiyatlardan oynadığınızda, yürütme koşulları sağlandığında tanımlı bir kâr marjı kilitleyebilirsiniz."
          },
          {
            name: "Value Bets",
            description:
              "Yanlış fiyatlanmış çizgiler ve pozitif beklenen değer sunan fırsatları tespit edin. Tekil bahisler hâlâ kaybedebilir, ancak daha büyük bir örneklemde disiplinli value betting uzun vadeli kâr hedefler. Bahis başına ortalama avantaj genellikle %3 ile %10 arasındadır."
          },
          {
            name: "Steam Move",
            description:
              "Çizgi değişmeden ve piyasa hareketi olağandışı derin göründüğünde, daha keskin bahis sitelerinden anormal oran düşüşlerini yakalayın. Fiyat; maç akışı, sakatlık veya kırmızı kart baskısı olmadan düştüğünde bu sinyaller daha anlamlı hale gelir. Bu durumlarda piyasa «havada bir şey var» işaretleri verebilir."
          }
        ]
      },
      login: { title: "Giriş", subtitle: "Gerçek zamanlı arbitrage, value betting ve steam move gösterge panonuza erişin." },
      support: {
        title: "Destek",
        intro: "Yardıma mı ihtiyacınız var? Destek ekibimizle doğrudan iletişime geçin.",
        telegram: "Telegram",
        email: "E-posta",
        contactSupport: "Destekle iletişim",
        fallback: "Destek bilgileri yakında paylaşılacak."
      }
    },
    "zh-CN": {
      header: {
        language: "语言",
        packages: "套餐",
        features: "功能",
        support: "支持",
        login: "登录"
      },
      common: { close: "关闭" },
      packages: {
        registerLine: "请联系客服注册。",
        basic: {
          description: [
            "套利访问",
            "实时数据",
            "对比全球19家主要博彩公司的赔率"
          ],
          price: "价格：399 USD"
        },
        pro: {
          description: [
            "包含 Basic",
            "解锁 Value Bets",
            "查看投注方式，尽量避免博彩公司限额检测系统"
          ],
          price: "价格：999 USD"
        },
        premium: {
          description: [
            "包含 Basic 与 Pro",
            "解锁 Steam Move",
            "优先支持",
            "发现机会时通过 Telegram 提醒"
          ],
          price: "价格：1599 USD"
        }
      },
      features: {
        title: "功能",
        items: [
          {
            name: "Arbitrage",
            description:
              "对比全球19家主要博彩公司的实时赔率，发现双向套利机会。在合适价位同时下注两边，当执行条件满足时，可锁定确定的利润空间。"
          },
          {
            name: "Value Bets",
            description:
              "识别报价错误、具备正期望值的机会。单笔投注仍可能亏损，但在更大样本下，纪律性的价值投注旨在实现长期盈利。每笔投注的平均优势通常在3%至10%之间。"
          },
          {
            name: "Steam Move",
            description:
              "当盘口不变而市场波动显得异常剧烈时，检测来自更敏锐博彩公司的异常赔率下跌。当比赛走势、伤病或红牌并未带来明显压力而价格却下跌时，这些信号更有意义。此时市场可能暗示「山雨欲来」。"
          }
        ]
      },
      login: {
        title: "登录",
        subtitle: "访问你的实时套利、价值投注和 Steam Move 控制台。"
      },
      support: {
        title: "支持",
        intro: "需要帮助？请直接联系我们的支持团队。",
        telegram: "Telegram",
        email: "电子邮件",
        contactSupport: "联系支持",
        fallback: "支持详情即将提供。"
      }
    },
    "th-TH": {
      header: {
        language: "ภาษา",
        packages: "แพ็กเกจ",
        features: "ฟีเจอร์",
        support: "ช่วยเหลือ",
        login: "เข้าสู่ระบบ"
      },
      common: { close: "ปิด" },
      packages: {
        registerLine: "ติดต่อฝ่ายสนับสนุนเพื่อลงทะเบียน",
        basic: {
          description: [
            "เข้าถึง Arbitrage",
            "ข้อมูลแบบเรียลไทม์",
            "เปรียบเทียบราคาจาก 19 บุ๊กมาร์กใหญ่ทั่วโลก"
          ],
          price: "ราคา: 399 USD"
        },
        pro: {
          description: [
            "รวม Basic",
            "ปลดล็อก Value Bets",
            "ดูวิธีเดิมพันเพื่อหลีกเลี่ยงระบบตรวจจับลิมิตของบุ๊กมาร์ก"
          ],
          price: "ราคา: 999 USD"
        },
        premium: {
          description: [
            "รวม Basic และ Pro",
            "ปลดล็อก Steam Move",
            "ซัพพอร์ตแบบเร่งด่วน",
            "แจ้งเตือน Telegram เมื่อพบโอกาส"
          ],
          price: "ราคา: 1599 USD"
        }
      },
      features: {
        title: "ฟีเจอร์",
        items: [
          {
            name: "Arbitrage",
            description:
              "เปรียบเทียบราคาต่อรองแบบเรียลไทม์จาก 19 บุ๊กมาร์กใหญ่ทั่วโลก และระบุโอกาสอาร์บิทราจแบบสองฝั่ง เมื่อวางเดิมพันทั้งสองฝั่งในราคาที่เหมาะสม ผู้ใช้สามารถล็อกกำไรตามที่กำหนดได้เมื่อเงื่อนไขการดำเนินการเป็นไปตามที่กำหนด"
          },
          {
            name: "Value Bets",
            description:
              "ระบุบุ๊กมาร์กที่เสนอเส้นที่ราคาผิดพลาดและโอกาสที่มีมูลค่าคาดหวังเป็นบวก การเดิมพันแต่ละครั้งยังอาจแพ้ได้ แต่เมื่อมีขนาดตัวอย่างมากขึ้น การเดิมพันตามมูลค่าอย่างมีวินัยจัดทำขึ้นเพื่อสร้างกำไรระยะยาว โดยขอบเฉลี่ยต่อการเดิมพันมักอยู่ระหว่าง 3% ถึง 10%"
          },
          {
            name: "Steam Move",
            description:
              "ตรวจจับการร่วงลงของราคาที่ผิดปกติจากบุ๊กมาร์กที่คมกว่า เมื่อเส้นไม่เปลี่ยนและการเคลื่อนไหวของตลาดดูลึกผิดปกติ สัญญาณเหล่านี้มีความหมายมากขึ้นเมื่อราคาลดลงโดยไม่มีแรงกดดันชัดเจนจากจังหวะการแข่งขัน การบาดเจ็บ หรือใบแดง ในกรณีเช่นนี้ ตลาดอาจบ่งบอกว่า «มีอะไรบางอย่างในอากาศ»"
          }
        ]
      },
      login: { title: "เข้าสู่ระบบ", subtitle: "เข้าถึงแดชบอร์ด arbitrage แบบเรียลไทม์, value betting และ steam move" },
      support: {
        title: "ช่วยเหลือ",
        intro: "ต้องการความช่วยเหลือ? ติดต่อทีมซัพพอร์ตของเราโดยตรง",
        telegram: "Telegram",
        email: "อีเมล",
        contactSupport: "ติดต่อฝ่ายสนับสนุน",
        fallback: "รายละเอียดการติดต่อจะประกาศเร็วๆ นี้"
      }
    },
    "vi-VN": {
      header: {
        language: "Ngôn ngữ",
        packages: "Bảng Giá",
        features: "Tính năng",
        support: "Hỗ trợ",
        login: "Đăng nhập"
      },
      common: { close: "Đóng" },
      packages: {
        intro: "Chọn gói phù hợp với nhu cầu sử dụng của bạn.",
        visiblePlans: ["basic", "pro", "premium"],
        basic: {
          badge: "Dùng thử 24 giờ",
          name: "Gói Basic",
          price: "Miễn phí",
          billingCycle: "/ 24 giờ",
          tagline: "Trải nghiệm các công cụ phân tích cơ bản trước khi nâng cấp.",
          benefitsTitle: "Quyền lợi",
          benefits: [
            "Truy cập Chênh Giá và Kèo Giữa.",
            "Dữ liệu có độ trễ khoảng 30 giây.",
            "Không hỗ trợ qua Telegram.",
            "Tự động hết hạn sau đúng 24 giờ."
          ]
        },
        pro: {
          name: "Gói Pro",
          price: "399 USD",
          billingCycle: "/ tháng",
          tagline:
            "Phù hợp với người dùng cần thêm công cụ phân tích và dữ liệu cập nhật nhanh.",
          benefitsTitle: "Quyền lợi",
          benefits: [
            "Truy cập Giá Trị, Chênh Giá và Kèo Giữa.",
            "Dữ liệu được cập nhật nhanh.",
            "Được ưu tiên hỗ trợ khách hàng qua Telegram.",
            "Không hỗ trợ liên kết Telegram để nhận thông báo."
          ],
          paymentButton: "💳 Thanh toán gói Pro",
          paymentAmount: "399 USDT"
        },
        premium: {
          badge: "Đề xuất",
          name: "Gói Premium",
          price: "799 USD",
          billingCycle: "/ tháng",
          tagline:
            "Dành cho người dùng chuyên sâu cần đầy đủ công cụ, dữ liệu nhanh và thông báo Telegram.",
          benefitsTitle: "Quyền lợi",
          benefits: [
            "Truy cập đầy đủ Biến Động, Giá Trị, Chênh Giá và Kèo Giữa.",
            "Dữ liệu được ưu tiên cập nhật nhanh nhất.",
            "Được ưu tiên hỗ trợ khách hàng qua Telegram.",
            "Hỗ trợ liên kết Telegram để nhận thông báo.",
            "Được tự cài đặt và quản lý thông báo Telegram."
          ],
          paymentButton: "💳 Thanh toán gói Premium",
          paymentAmount: "799 USDT"
        },
        payment: {
          backToPricing: "← Quay lại Bảng Giá",
          title: "Thanh toán",
          selectedPlan: "Gói đã chọn",
          planPrice: "Giá gói",
          paymentAmount: "Số tiền cần thanh toán",
          currency: "USDT",
          network: "BSC — BNB Smart Chain (BEP20)",
          qrTitle: "Quét mã QR để lấy địa chỉ ví",
          qrNote:
            "Mã QR chỉ chứa địa chỉ ví. Vui lòng tự nhập đúng số tiền của gói đã chọn.",
          addressTitle: "Địa chỉ nhận thanh toán",
          copyAddress: "📋 Sao chép địa chỉ ví USDT",
          copied: "✅ Đã sao chép địa chỉ ví",
          copyFailed:
            "⚠️ Không thể tự sao chép. Hãy chọn và sao chép địa chỉ phía trên.",
          warningTitle: "⚠️ Lưu ý quan trọng",
          warnings: [
            "Chỉ gửi USDT qua mạng BSC (BEP20).",
            "Không gửi qua TRC20, ERC20 hoặc mạng lưới khác.",
            "Vui lòng kiểm tra kỹ địa chỉ, mạng lưới và số tiền trước khi xác nhận giao dịch.",
            "Chuyển sai mạng hoặc sai địa chỉ có thể khiến tài sản không thể khôi phục."
          ],
          receiptText:
            "Sau khi thanh toán, vui lòng gửi ảnh biên lai hoặc mã giao dịch (TXID) cho nhân viên hỗ trợ để xác nhận và kích hoạt gói.",
          contactSupport: "💬 Gửi biên lai/TXID cho nhân viên hỗ trợ",
          bankTitle: "🏦 Thanh toán bằng chuyển khoản ngân hàng",
          bankText:
            "Vui lòng liên hệ nhân viên hỗ trợ để được cung cấp thông tin chuyển khoản ngân hàng.",
          manualActivation:
            "Thanh toán không tự động kích hoạt tài khoản. Nhân viên hỗ trợ sẽ kiểm tra giao dịch trước khi kích hoạt gói."
        },
        registerLine: ""
      },
      features: {
        title: "Cách tìm cơ hội",
        intro:
          "Phần mềm giúp tổng hợp, so sánh và lọc dữ liệu tỷ lệ cược. Công cụ không tự đặt cược và không đảm bảo lợi nhuận. Người dùng vẫn cần kiểm tra đúng trận, đúng thị trường, thời điểm cập nhật, giới hạn cược và quản lý vốn trước khi quyết định.",
        detailLabel: "Xem cách hoạt động và ví dụ",
        items: [
          {
            name: "📈 Biến Động",
            description:
              "Theo dõi line và odds theo thời gian để phát hiện những chuyển động nhanh hoặc bất thường của thị trường.",
            sections: [
              {
                title: "Cách hoạt động",
                paragraphs: [
                  "Hệ thống theo dõi line và tỷ lệ cược theo thời gian, sau đó phát hiện những thay đổi nhanh hoặc khác thường tại các nhà cái đáng tin cậy. Tín hiệu có thể xuất hiện trước trận hoặc trong lúc trận đấu diễn ra."
                ]
              },
              {
                title: "Lợi nhuận tiềm năng đến từ đâu?",
                paragraphs: [
                  "Khi một nhà cái cập nhật giá nhanh hơn thị trường, những nhà cái còn lại có thể chưa điều chỉnh kịp. Người dùng có thể nhận biết hướng dịch chuyển sớm và tìm mức giá tốt hơn còn sót lại ở nhà cái cập nhật chậm."
                ]
              },
              {
                title: "Ví dụ",
                paragraphs: [
                  "Odds cửa Tài 2.5 tại một nhà cái lớn giảm từ 2.05 xuống 1.80, trong khi một nhà cái khác vẫn giữ 2.00. Điều này có thể cho thấy thị trường đang đánh giá xác suất cửa Tài cao hơn trước, còn mức 2.00 có khả năng là giá chưa cập nhật."
                ]
              },
              {
                title: "Cách sử dụng",
                bullets: [
                  "Kiểm tra tín hiệu thuộc trước trận hay trực tiếp.",
                  "Đảm bảo đang so sánh đúng FT, hiệp 1 và đúng loại thị trường.",
                  "So sánh cùng line tại nhiều nhà cái.",
                  "Ưu tiên biến động xuất hiện đồng thời ở nhiều nguồn đáng tin cậy.",
                  "Không đặt cược chỉ vì odds giảm; cần kiểm tra tin tức, đội hình và thanh khoản."
                ]
              }
            ],
            note:
              "Biến Động là công cụ phát hiện chuyển động thị trường, không phải tín hiệu đảm bảo cửa nào sẽ thắng."
          },
          {
            name: "💰 Giá Trị",
            description:
              "Tìm mức odds cao hơn giá trị hợp lý để xác định những cơ hội có kỳ vọng dài hạn dương.",
            sections: [
              {
                title: "Cách hoạt động",
                paragraphs: [
                  "Hệ thống ước tính xác suất hợp lý của một lựa chọn sau khi xử lý biên lợi nhuận của nhà cái, rồi so sánh xác suất đó với odds đang được cung cấp. Một cơ hội Giá Trị xuất hiện khi mức odds thực tế cao hơn mức odds hợp lý."
                ]
              },
              {
                title: "Lợi nhuận tiềm năng đến từ đâu?",
                paragraphs: [
                  "Lợi thế đến từ việc mua một xác suất với giá tốt hơn giá trị thực. Một kèo Giá Trị vẫn có thể thua riêng lẻ, nhưng nếu phép tính chính xác và được lặp lại trên nhiều cược độc lập, tổng kết dài hạn có thể tạo lợi nhuận kỳ vọng dương."
                ]
              },
              {
                title: "Ví dụ",
                paragraphs: [
                  "Odds 2.20 tương đương xác suất hòa vốn khoảng 45,45%. Nếu xác suất hợp lý sau khi phân tích là 50%, EV lý thuyết là: EV = 50% × 2.20 − 1 = +10%.",
                  "Điều này không có nghĩa mỗi cược sẽ lãi 10%. Nó chỉ thể hiện mức giá đang có lợi thế kỳ vọng nếu xác suất 50% là chính xác và chiến lược được thực hiện đủ nhiều lần."
                ]
              },
              {
                title: "Cách sử dụng",
                bullets: [
                  "Ưu tiên cơ hội có EV dương rõ ràng, không chọn chỉ vì odds cao.",
                  "So sánh đúng trận, đúng line và đúng thời điểm.",
                  "Chọn nhà cái có mức odds tốt nhất cho cùng lựa chọn.",
                  "Chia vốn nhỏ và đồng đều; tránh tăng tiền cược chỉ vì một vài lần thua.",
                  "Đánh giá kết quả trên một mẫu cược đủ lớn, không dựa vào một trận."
                ]
              }
            ]
          },
          {
            name: "⚖️ Chênh Giá",
            description:
              "So sánh giá của các kết quả đối nghịch để tìm chênh lệch có thể phân bổ vốn cho cả hai phía.",
            sections: [
              {
                title: "Cách hoạt động",
                paragraphs: [
                  "Hệ thống so sánh giá của các kết quả đối nghịch trên cùng một trận, cùng thị trường, cùng line và cùng thời điểm. Khi tổng xác suất quy đổi từ những odds tốt nhất nhỏ hơn 100%, có thể xuất hiện cơ hội phân bổ tiền vào các cửa để tạo mức hoàn trả gần như cố định.",
                  "Điều kiện cơ bản đối với thị trường hai cửa: 1 / Odds A + 1 / Odds B < 1."
                ]
              },
              {
                title: "Lợi nhuận tiềm năng đến từ đâu?",
                paragraphs: [
                  "Lợi nhuận đến từ sự khác biệt giá giữa các nhà cái, không phụ thuộc vào dự đoán đội nào thắng, miễn là các lệnh đều được đặt đủ số tiền tại đúng odds đã tính."
                ]
              },
              {
                title: "Ví dụ",
                paragraphs: [
                  "Nhà cái A có odds 2.10 cho cửa thứ nhất và nhà cái B có odds 2.05 cho cửa đối diện.",
                  "1 / 2.10 + 1 / 2.05 ≈ 0,964. Vì kết quả nhỏ hơn 1, đây là chênh lệch dương. Nếu phân bổ tổng vốn 1.000.000 đồng theo đúng tỷ lệ, mức hoàn trả lý thuyết khoảng 1.037.000 đồng, tương đương lợi nhuận gộp khoảng 3,7% trước phí và sai lệch khi khớp lệnh."
                ]
              },
              {
                title: "Cách sử dụng",
                bullets: [
                  "Kiểm tra hai lựa chọn thực sự đối nghịch và bao phủ đầy đủ kết quả.",
                  "Xác nhận đúng line, đúng FT/hiệp và đúng luật tính cược của từng nhà cái.",
                  "Kiểm tra hạn mức trước khi đặt lệnh đầu tiên.",
                  "Tính sẵn số tiền cho từng cửa.",
                  "Đặt các lệnh nhanh vì odds có thể thay đổi bất cứ lúc nào."
                ]
              }
            ],
            note:
              "Rủi ro chính là odds thay đổi giữa hai lần đặt, giới hạn tài khoản, hủy cược hoặc hai nhà cái áp dụng luật thanh toán khác nhau."
          },
          {
            name: "🎯 Kèo Giữa",
            description:
              "Tìm khoảng chênh giữa hai line để hai cửa đối nghịch có khả năng cùng thắng khi kết quả rơi vào vùng giữa.",
            sections: [
              {
                title: "Cách hoạt động",
                paragraphs: [
                  "Hệ thống tìm hai line khác nhau trên cùng một thị trường, sau đó xác định khoảng kết quả nằm giữa hai line. Người dùng đặt hai cửa đối nghịch tại hai nhà cái khác nhau. Nếu kết quả rơi đúng vào khoảng giữa, cả hai vé có thể cùng thắng."
                ]
              },
              {
                title: "Lợi nhuận tiềm năng đến từ đâu?",
                paragraphs: [
                  "Lợi nhuận lớn xuất hiện khi kết quả rơi vào vùng giữa. Nếu kết quả nằm ngoài vùng này, thông thường một vé thắng và một vé thua, tạo ra một khoản chi phí nhỏ. Vì vậy, chỉ nên chọn khi xác suất kết quả rơi vào vùng giữa đủ lớn so với chi phí đó."
                ]
              },
              {
                title: "Ví dụ",
                paragraphs: [
                  "Nhà cái A: Tài 2.5, odds 1.90. Nhà cái B: Xỉu 3.5, odds 1.90.",
                  "Nếu trận đấu có đúng 3 bàn, cả Tài 2.5 và Xỉu 3.5 đều thắng.",
                  "Nếu đặt 100.000 đồng mỗi cửa: đúng 3 bàn thì tổng nhận 380.000 đồng, lợi nhuận 180.000 đồng. Nếu trận có từ 0–2 bàn hoặc từ 4 bàn trở lên, một vé thắng và một vé thua; tổng nhận 190.000 đồng, chi phí 10.000 đồng."
                ]
              },
              {
                title: "Cách sử dụng",
                bullets: [
                  "Xác nhận hai line thuộc cùng trận và cùng thị trường.",
                  "Kiểm tra khoảng giữa có kết quả thực tế nào có thể xảy ra.",
                  "So sánh lợi nhuận khi trúng vùng giữa với chi phí khi trượt vùng giữa.",
                  "Ưu tiên thị trường có kết quả giữa xuất hiện với xác suất hợp lý.",
                  "Kiểm tra luật hòa tiền, nửa thắng/nửa thua đối với line châu Á."
                ]
              }
            ]
          }
        ],
        usageTitle: "Cách dùng bộ lọc hiệu quả",
        usageBullets: [
          "Phạm vi: chọn tín hiệu trước trận, trực tiếp hoặc cả hai.",
          "Ngưỡng tối thiểu: chỉ nhận cơ hội đạt mức EV hoặc lợi nhuận mong muốn.",
          "Nhà cái: chọn những nhà cái bạn thực sự có tài khoản và có thể đặt cược.",
          "Telegram: bật thông báo riêng cho từng công cụ để không bỏ lỡ cơ hội phù hợp.",
          "Quản lý vốn: chỉ sử dụng một phần nhỏ tổng vốn cho mỗi cơ hội và không cố gỡ lỗ."
        ],
        riskTitle: "Lưu ý rủi ro",
        riskText:
          "Tỷ lệ cược có thể thay đổi nhanh; dữ liệu có thể trễ; nhà cái có thể giới hạn, hủy hoặc thanh toán cược theo luật khác nhau. Các con số hiển thị là công cụ hỗ trợ phân tích, không phải cam kết lợi nhuận. Chỉ tham gia khi đủ tuổi, hiểu rõ rủi ro và sử dụng số tiền có thể chấp nhận mất."
      },
      login: { title: "Đăng nhập", subtitle: "Truy cập bảng điều khiển arbitrage thời gian thực, value betting và steam move." },
      support: {
        title: "Hỗ trợ",
        intro:
          "Bạn cần hỗ trợ về tài khoản, gói dịch vụ, thanh toán hoặc cách sử dụng? Hãy chọn một kênh Telegram bên dưới.",
        telegram: "Telegram",
        email: "Email",
        contactSupport: "Liên hệ hỗ trợ",
        fallback: "Thông tin hỗ trợ sẽ sớm được cập nhật.",
        contactHint:
          "Nhấn vào ID Telegram để mở đúng tài khoản hoặc bot hỗ trợ.",
        agentLabel: "Nhân viên hỗ trợ Telegram",
        agentDescription:
          "Trao đổi trực tiếp với nhân viên khi cần tư vấn hoặc xử lý vấn đề về tài khoản, gói dịch vụ và thanh toán.",
        botLabel: "Bot hỗ trợ TKB Sport",
        botDescription:
          "Xem hướng dẫn sử dụng, bảng giá, thanh toán và các nội dung hỗ trợ tự động."
      }
    },
    "ko-KR": {
      header: {
        language: "언어",
        packages: "플랜",
        features: "기능",
        support: "고객 지원",
        login: "로그인"
      },
      common: { close: "닫기" },
      packages: {
        registerLine: "등록하려면 고객 지원에 문의하세요.",
        basic: {
          description: [
            "Arbitrage 이용",
            "실시간 데이터",
            "전 세계 19개 주요 북메이커의 배당 비교"
          ],
          price: "가격: 399 USD"
        },
        pro: {
          description: [
            "Basic 포함",
            "Value Bets 잠금 해제",
            "북메이커 한도 감지 시스템을 피하는 베팅 방법 확인"
          ],
          price: "가격: 999 USD"
        },
        premium: {
          description: [
            "Basic 및 Pro 포함",
            "Steam Move 잠금 해제",
            "우선 지원",
            "기회가 감지되면 Telegram 알림"
          ],
          price: "가격: 1599 USD"
        }
      },
      features: {
        title: "기능",
        items: [
          {
            name: "Arbitrage",
            description:
              "전 세계 19개 주요 북메이커의 실시간 배당을 비교하고 양방향 차익거래 기회를 식별합니다. 적정 가격에 양쪽에 베팅하면 실행 조건이 충족될 때 정해진 수익 마진을 확정할 수 있습니다."
          },
          {
            name: "Value Bets",
            description:
              "잘못 책정된 라인과 양의 기대값을 제공하는 북메이커와 기회를 식별합니다. 개별 베팅은 여전히 질 수 있지만, 표본이 커질수록 규율 있는 밸류 베팅은 장기 수익을 목표로 설계됩니다. 베팅당 평균 엣지는 보통 3%에서 10% 사이입니다."
          },
          {
            name: "Steam Move",
            description:
              "라인은 그대로인데 시장 움직임이 유난히 깊어 보일 때, 더 예리한 북메이커에서 비정상적인 배당 하락을 감지합니다. 경기 흐름·부상·레드카드 등으로 가격 하락 원인이 분명하지 않을 때 이런 신호는 더 의미가 있습니다. 이때 시장은 무언가 심상치 않은 기운을 보일 수 있습니다."
          }
        ]
      },
      login: { title: "로그인", subtitle: "실시간 arbitrage, value betting, steam move 대시보드에 액세스하세요." },
      support: {
        title: "고객 지원",
        intro: "도움이 필요하신가요? 지원팀에 직접 문의하세요.",
        telegram: "Telegram",
        email: "이메일",
        contactSupport: "지원 문의",
        fallback: "지원 정보는 곧 안내됩니다."
      }
    },
    "en-SG": {
      header: {
        language: "Language",
        packages: "Packages",
        features: "Features",
        support: "Support",
        login: "Login"
      },
      common: { close: "Close" },
      packages: base.packages,
      features: base.features,
      login: base.login,
      support: base.support
    },
    "id-ID": {
      header: {
        language: "Bahasa",
        packages: "Paket",
        features: "Fitur",
        support: "Dukungan",
        login: "Masuk"
      },
      common: { close: "Tutup" },
      packages: {
        registerLine: "Hubungi dukungan untuk mendaftar.",
        basic: {
          description: [
            "Akses Arbitrage",
            "Data realtime",
            "Bandingkan odds dari 19 bandar besar di seluruh dunia"
          ],
          price: "Harga: 399 USD"
        },
        pro: {
          description: [
            "Termasuk Basic",
            "Buka Value Bets",
            "Lihat metode taruhan untuk menghindari sistem deteksi limit bandar"
          ],
          price: "Harga: 999 USD"
        },
        premium: {
          description: [
            "Termasuk Basic dan Pro",
            "Buka Steam Move",
            "Dukungan prioritas",
            "Peringatan Telegram saat peluang terdeteksi"
          ],
          price: "Harga: 1599 USD"
        }
      },
      features: {
        title: "Fitur",
        items: [
          {
            name: "Arbitrage",
            description:
              "Bandingkan odds secara real-time dari 19 bandar besar di seluruh dunia dan identifikasi peluang arbitrase dua arah. Dengan memasang kedua sisi pada harga yang tepat, pengguna dapat mengunci margin keuntungan yang ditetapkan ketika kondisi eksekusi terpenuhi."
          },
          {
            name: "Value Bets",
            description:
              "Identifikasi bandar yang menawarkan garis salah harga dan peluang nilai harapan positif. Taruhan individual masih bisa kalah, tetapi pada sampel yang lebih besar, value betting disiplin dirancang untuk menghasilkan keuntungan jangka panjang. Rata-rata edge per taruhan biasanya antara 3% hingga 10%."
          },
          {
            name: "Steam Move",
            description:
              "Deteksi penurunan odds abnormal dari bandar yang lebih tajam ketika garis tidak berubah dan pergerakan pasar tampak luar biasa dalam. Sinyal ini lebih bermakna ketika harga turun tanpa tekanan jelas dari alur pertandingan, cedera, atau kartu merah. Dalam kasus itu, pasar dapat menunjukkan bahwa «ada sesuatu di udara»."
          }
        ]
      },
      login: { title: "Masuk", subtitle: "Akses dasbor arbitrase real-time, value betting, dan steam move." },
      support: {
        title: "Dukungan",
        intro: "Butuh bantuan? Hubungi tim dukungan kami secara langsung.",
        telegram: "Telegram",
        email: "Email",
        contactSupport: "Hubungi dukungan",
        fallback: "Detail dukungan akan segera tersedia."
      }
    },
    "nl-NL": {
      header: {
        language: "Taal",
        packages: "Pakketten",
        features: "Functies",
        support: "Ondersteuning",
        login: "Inloggen"
      },
      common: { close: "Sluiten" },
      packages: {
        registerLine: "Neem contact op met support om te registreren.",
        basic: {
          description: [
            "Arbitrage-toegang",
            "Realtime data",
            "Vergelijk odds van 19 grote bookmakers wereldwijd"
          ],
          price: "Prijs: 399 USD"
        },
        pro: {
          description: [
            "Inclusief Basic",
            "Ontgrendelt Value Bets",
            "Bekijk inzetmethoden om limietdetectie van bookmakers te vermijden"
          ],
          price: "Prijs: 999 USD"
        },
        premium: {
          description: [
            "Inclusief Basic en Pro",
            "Ontgrendelt Steam Move",
            "Prioriteitsondersteuning",
            "Telegram-meldingen wanneer kansen worden gedetecteerd"
          ],
          price: "Prijs: 1599 USD"
        }
      },
      features: {
        title: "Functies",
        items: [
          {
            name: "Arbitrage",
            description:
              "Vergelijk realtime odds van 19 grote bookmakers wereldwijd en identificeer tweerichtings-arbitragemogelijkheden. Door beide kanten tegen de juiste prijzen te plaatsen, kunnen gebruikers een vastgestelde winstmarge vastzetten wanneer de uitvoeringsvoorwaarden zijn vervuld."
          },
          {
            name: "Value Bets",
            description:
              "Herken bookmakers met verkeerd geprijsde lijnen en kansen met positieve verwachte waarde. Individuele weddenschappen kunnen nog steeds verliezen, maar over een grotere steekproef is gedisciplineerd value betting bedoeld om langetermijnwinst te genereren. De gemiddelde edge per weddenschap ligt doorgaans tussen 3% en 10%."
          },
          {
            name: "Steam Move",
            description:
              "Detecteer abnormale oddsdalingen bij scherpere bookmakers wanneer de lijn ongewijzigd blijft en de marktbeweging ongewoon diep lijkt. Deze signalen zijn zinvoller wanneer de prijs daalt zonder duidelijke druk van wedstrijdverloop, blessures of rode kaarten. Dan kan de markt tekenen tonen dat er «iets in de lucht hangt»."
          }
        ]
      },
      login: { title: "Inloggen", subtitle: "Toegang tot je realtime arbitrage-, value betting- en steam move-dashboard." },
      support: {
        title: "Ondersteuning",
        intro: "Hulp nodig? Neem rechtstreeks contact op met ons supportteam.",
        telegram: "Telegram",
        email: "E-mail",
        contactSupport: "Contact opnemen",
        fallback: "Supportgegevens volgen binnenkort."
      }
    }
  };

  const locale = map[code];
  if (!locale) return base;

  return {
    ...base,
    ...locale,
    header: { ...base.header, ...(locale.header || {}) },
    common: { ...base.common, ...(locale.common || {}) },
    packages: { ...base.packages, ...(locale.packages || {}) },
    features: {
      ...base.features,
      ...(locale.features || {}),
      items: (locale.features && locale.features.items) || base.features.items
    },
    login: { ...base.login, ...(locale.login || {}) },
    support: { ...base.support, ...(locale.support || {}) }
  };
}

// Small inline icon helpers for the password visibility toggle. No external
// icon library, no emoji, no images — plain SVG using currentColor so it
// inherits the toggle button's color/hover/disabled states.
function AnalysisLoginEyeIcon() {
  return (
    <svg
      className="analysis-login-password-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function AnalysisLoginEyeOffIcon() {
  return (
    <svg
      className="analysis-login-password-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 3l18 18" />
      <path d="M10.58 10.58a3 3 0 0 0 4.24 4.24" />
      <path d="M9.88 5.09A10.94 10.94 0 0 1 12 5c7 0 10.5 7 10.5 7a13.16 13.16 0 0 1-2.16 3.02M6.61 6.61C3.9 8.36 1.5 12 1.5 12S5 19 12 19a10.9 10.9 0 0 0 3.39-.56" />
    </svg>
  );
}

export default function AnalysisLogin() {
  const [account, setAccount] = useState(""); // email
  const [password, setPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const [kickedReason] = useState(() => {
    const reason = safeGetLocalStorage(ANALYSIS_SESSION_KICKED_KEY, "");
    safeRemoveLocalStorage(ANALYSIS_SESSION_KICKED_KEY);
    return reason;
  });

  const [activeSection, setActiveSection] = useState(() => (kickedReason ? "login" : null));

  // Pro/Premium USDT (BEP20) checkout screen shown inside the Packages panel.
  // null = plan list; "pro" | "premium" = checkout screen for that plan.
  const [selectedPaymentPlanKey, setSelectedPaymentPlanKey] = useState(null);
  const [paymentCopyStatus, setPaymentCopyStatus] = useState("idle");

  // Leaving the Packages panel always drops back to the plan list next time
  // it is opened — never resume on the checkout screen.
  useEffect(() => {
    if (activeSection !== "packages") {
      setSelectedPaymentPlanKey(null);
      setPaymentCopyStatus("idle");
    }
  }, [activeSection]);

  function openPayment(planKey) {
    if (planKey !== "pro" && planKey !== "premium") {
      return;
    }

    setSelectedPaymentPlanKey(planKey);
    setPaymentCopyStatus("idle");

    if (typeof document !== "undefined") {
      const body = document.querySelector(".analysis-login-body");

      if (body && typeof body.scrollTo === "function") {
        body.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }

  function closePayment() {
    setSelectedPaymentPlanKey(null);
    setPaymentCopyStatus("idle");
  }

  async function handleCopyPaymentAddress() {
    const copied = await copyTextToClipboard(PAYMENT_USDT_BSC_ADDRESS);
    setPaymentCopyStatus(copied ? "copied" : "error");
  }

  // "login" | "register" — which form is shown inside the login panel.
  // Reset back to "login" whenever the panel itself is closed/reopened.
  const [authMode, setAuthMode] = useState("login");
  useEffect(() => {
    if (activeSection !== "login") setAuthMode("login");
  }, [activeSection]);

  // Password must always start hidden again once the login panel is
  // closed/reopened, or once the user switches away from the login form
  // (e.g. into register). This never clears the typed password value.
  useEffect(() => {
    if (activeSection !== "login" || authMode !== "login") {
      setShowLoginPassword(false);
    }
  }, [activeSection, authMode]);

  const [lang, setLang] = useState(() => {
    const saved = safeGetLocalStorage(LANG_KEY, DEFAULT_LANG);
    return resolveSavedLang(saved);
  });

  const t = useMemo(() => getLoginTranslations(lang), [lang]);

  // Payment checkout flow is only wired up for vi-VN in this step: other
  // locales still use the legacy 999/1599 USD pricing text.
  const selectedPaymentPlan =
    lang === "vi-VN" &&
    (selectedPaymentPlanKey === "pro" || selectedPaymentPlanKey === "premium")
      ? t.packages?.[selectedPaymentPlanKey]
      : null;

  const selectedLangOption =
    LANGUAGE_OPTIONS.find((o) => o.code === lang) ||
    LANGUAGE_OPTIONS.find((o) => o.code === DEFAULT_LANG);

  useEffect(() => {
    // Keep the exact storage + document-lang sync behavior used by AnalysisLayout.
    applyLangToDocument(lang);
    safeSetLocalStorage(LANG_KEY, lang);
    broadcastLangChange(lang);
  }, [lang]);

  // Exact session-creation logic previously inline in onSubmit — factored
  // out so the self-registration flow can create an identical analysis
  // session via the same code path, without duplicating it.
  async function finalizeAnalysisLoginSession(user) {
    const deviceId = getOrCreateAnalysisDeviceId();
    const sessionId = createAnalysisSessionId();
    safeSetLocalStorage(ANALYSIS_SESSION_ID_KEY, sessionId);

    if (!db) {
      safeRemoveLocalStorage(ANALYSIS_SESSION_ID_KEY);
      safeRemoveLocalStorage(ANALYSIS_LAST_ACTIVITY_AT_KEY);
      // Do NOT call signOut(auth) here: Firebase Auth is shared across tabs in the same
      // browser profile. Signing out here would also kick any open admin tab.
      setErr("Could not create a secure session. Please try again.");
      return false;
    }

    try {
      await setDoc(
        doc(db, "analysisSessions", user.uid),
        {
          uid: user.uid,
          email: user.email || "",
          activeSessionId: sessionId,
          activeDeviceId: deviceId,
          updatedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp(),
          createdBy: "analysis-login"
        },
        { merge: true }
      );
    } catch (fsErr) {
      safeRemoveLocalStorage(ANALYSIS_SESSION_ID_KEY);
      safeRemoveLocalStorage(ANALYSIS_LAST_ACTIVITY_AT_KEY);
      // Do NOT call signOut(auth) here: Firebase Auth is shared across browser tabs.
      // Signing out on a Firestore write failure (e.g. rules blocked) would also
      // sign out any open admin tab in the same browser. Instead, leave the user on
      // the login page with an error; the Analysis session key has been removed so
      // AnalysisLayout will not admit them if they somehow navigate to /analysis.
      // NOTE: Firestore rules must allow: match /analysisSessions/{uid} {
      //   allow read, create, update, delete: if request.auth != null && request.auth.uid == uid;
      // }
      // eslint-disable-next-line no-console
      console.error("[LOGIN SESSION]", fsErr);
      setErr("Could not create a secure session. Please try again.");
      return false;
    }

    navigate("/analysis", { replace: true });
    return true;
  }

  // Self-registration recovery: if a previous signup created the Firebase
  // account but the trial bootstrap call itself failed (network/500), this
  // repairs it on the next successful login — before any session/navigate.
  // Never runs for admin/super_admin, an already-Basic/Pro/Premium account,
  // an admin-created account (creator claims), or an account that already
  // has an access grant.
  async function recoverSelfRegistrationBootstrapIfNeeded(user) {
    try {
      const tokenResult = await user.getIdTokenResult();
      if (shouldBootstrapSelfRegistration(tokenResult.claims)) {
        await bootstrapSelfRegistrationTrial(user);
      }
    } catch (bootstrapErr) {
      // Best-effort repair only — never blocks login on failure.
      // eslint-disable-next-line no-console
      console.error("[LOGIN SELF-REGISTRATION RECOVERY]", bootstrapErr);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");

    if (!auth) {
      setErr("Firebase Auth chưa sẵn sàng (auth = null). Kiểm tra file src/utils/firebase.js");
      return;
    }

    const email = String(account || "").trim();
    // ✅ KHÔNG trim password (tránh mất dấu cách vô tình khi copy/paste)
    const pwd = String(password ?? "");

    if (!email || !pwd) {
      setErr("Vui lòng nhập đủ email và mật khẩu.");
      return;
    }

    try {
      setLoading(true);

      const cred = await signInWithEmailAndPassword(auth, email, pwd);
      console.log("[LOGIN OK]", cred.user?.uid, cred.user?.email);

      const user = cred.user;

      await recoverSelfRegistrationBootstrapIfNeeded(user);

      await finalizeAnalysisLoginSession(user);
    } catch (e) {
      const code = e?.code || "";
      let msg = e?.message || "Đăng nhập thất bại.";

      if (code === "auth/invalid-email") msg = "Email không đúng định dạng.";
      else if (code === "auth/user-not-found") msg = "Tài khoản không tồn tại trong Firebase.";
      else if (code === "auth/wrong-password") msg = "Sai mật khẩu.";
      else if (code === "auth/invalid-credential")
        msg = "Sai email hoặc mật khẩu (hoặc tài khoản chưa có Email/Password).";
      else if (code === "auth/too-many-requests")
        msg = "Bạn đăng nhập sai quá nhiều lần. Hãy chờ một lúc rồi thử lại.";
      else if (code === "auth/network-request-failed")
        msg = "Lỗi mạng. Kiểm tra internet hoặc thử lại.";

      setErr(msg);
      console.error("[LOGIN ERROR]", code, e);
    } finally {
      setLoading(false);
    }
  }

  // Called by AnalysisSelfRegistrationForm only after the trial bootstrap
  // call AND its internal force-refreshed getIdToken(true) have both
  // already succeeded — creates the analysis session via the exact same
  // logic used by the login flow above, then navigates to /analysis.
  async function handleSelfRegistrationComplete(user) {
    setErr("");
    await finalizeAnalysisLoginSession(user);
  }

  return (
    <div
      className="analysis-login-page"
      style={{ backgroundImage: `url(${bgLogin})` }}
    >
      <div className="analysis-login-topbar" role="navigation" aria-label="Login header">
        <div className="analysis-login-topbar-inner">
          <button
            type="button"
            className={`analysis-login-topbar-item ${
              activeSection === "language" ? "is-active" : ""
            }`}
            onClick={() => setActiveSection("language")}
          >
            <FlagIcon item={selectedLangOption} size={14} />
            <span className="analysis-login-topbar-text">{t.header.language}</span>
            <span className="analysis-login-topbar-caret" aria-hidden="true">
              ▼
            </span>
          </button>

          <button
            type="button"
            className={`analysis-login-topbar-item ${
              activeSection === "packages" ? "is-active" : ""
            }`}
            onClick={() => setActiveSection("packages")}
          >
            <span className="analysis-login-topbar-text">{t.header.packages}</span>
          </button>

          <button
            type="button"
            className={`analysis-login-topbar-item ${
              activeSection === "features" ? "is-active" : ""
            }`}
            onClick={() => setActiveSection("features")}
          >
            <span className="analysis-login-topbar-text">{t.header.features}</span>
          </button>

          <button
            type="button"
            className={`analysis-login-topbar-item ${
              activeSection === "support" ? "is-active" : ""
            }`}
            onClick={() => setActiveSection("support")}
          >
            <span className="analysis-login-topbar-text">{t.header.support}</span>
          </button>

          <button
            type="button"
            className={`analysis-login-topbar-item analysis-login-topbar-item--login ${
              activeSection === "login" ? "is-active" : ""
            }`}
            onClick={() => setActiveSection("login")}
          >
            <span className="analysis-login-topbar-text">{t.header.login}</span>
          </button>
        </div>
      </div>

      <div
        className={`analysis-login-body${
          activeSection === "login"
            ? " analysis-login-body--login"
            : activeSection
              ? " analysis-login-body--panels"
              : ""
        }`}
      >
        {activeSection === "language" && (
          <div
            className="analysis-login-card analysis-login-panel"
            role="region"
            aria-label="Language"
          >
            <div className="analysis-login-panel-header">
              <div className="analysis-login-panel-title">{t.header.language}</div>
              <button
                type="button"
                className="analysis-login-panel-close"
                onClick={() => setActiveSection(null)}
              >
                {t.common.close}
              </button>
            </div>

            <div className="analysis-login-lang-list" role="list">
              {LANGUAGE_OPTIONS.map((item) => {
                const active = item.code === lang;
                return (
                  <button
                    key={item.code}
                    type="button"
                    className={`analysis-login-lang-option ${
                      active ? "is-active" : ""
                    }`}
                    onClick={() => setLang(item.code)}
                  >
                    <FlagIcon item={item} size={14} />
                    <span className="analysis-login-lang-label">{item.label}</span>
                    <span className="analysis-login-lang-check" aria-hidden="true">
                      {active ? "*" : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {activeSection === "packages" && (
          <div
            className="analysis-login-card analysis-login-panel analysis-login-panel--packages"
            role="region"
            aria-label="Packages"
          >
            <div className="analysis-login-panel-header">
              <div className="analysis-login-panel-title">{t.header.packages}</div>
              <button
                type="button"
                className="analysis-login-panel-close"
                onClick={() => setActiveSection(null)}
              >
                {t.common.close}
              </button>
            </div>

            {selectedPaymentPlan ? (
              <div className="analysis-payment-view">
                <button
                  type="button"
                  className="analysis-payment-back"
                  onClick={closePayment}
                >
                  {t.packages.payment.backToPricing}
                </button>

                <div className="analysis-payment-heading">
                  {selectedPaymentPlan.paymentButton}
                </div>

                <div className="analysis-payment-summary">
                  <div className="analysis-payment-summary-row">
                    <span>{t.packages.payment.selectedPlan}</span>
                    <strong>{selectedPaymentPlan.name}</strong>
                  </div>
                  <div className="analysis-payment-summary-row">
                    <span>{t.packages.payment.planPrice}</span>
                    <strong>
                      {selectedPaymentPlan.price} {selectedPaymentPlan.billingCycle}
                    </strong>
                  </div>
                  <div className="analysis-payment-summary-row">
                    <span>{t.packages.payment.paymentAmount}</span>
                    <strong className="analysis-payment-amount">
                      {selectedPaymentPlan.paymentAmount}
                    </strong>
                  </div>
                  <div className="analysis-payment-summary-row">
                    <span>{t.packages.payment.currency}</span>
                    <strong className="analysis-payment-network">
                      {t.packages.payment.network}
                    </strong>
                  </div>
                </div>

                <div className="analysis-payment-qr-card">
                  <div className="analysis-payment-qr-title">{t.packages.payment.qrTitle}</div>
                  <img
                    src={paymentQr}
                    alt={`Mã QR thanh toán ${selectedPaymentPlan.name} bằng USDT qua mạng BSC (BEP20)`}
                    className="analysis-payment-qr-image"
                  />
                  <div className="analysis-payment-qr-note">{t.packages.payment.qrNote}</div>
                </div>

                <div className="analysis-payment-address-section">
                  <div className="analysis-payment-address-title">
                    {t.packages.payment.addressTitle}
                  </div>
                  <code className="analysis-payment-address">
                    {PAYMENT_USDT_BSC_ADDRESS}
                  </code>
                  <button
                    type="button"
                    className="analysis-payment-copy-button"
                    onClick={handleCopyPaymentAddress}
                  >
                    {t.packages.payment.copyAddress}
                  </button>

                  {paymentCopyStatus === "copied" ? (
                    <div className="analysis-payment-copy-status is-success" role="status">
                      {t.packages.payment.copied}
                    </div>
                  ) : null}

                  {paymentCopyStatus === "error" ? (
                    <div className="analysis-payment-copy-status is-error" role="alert">
                      {t.packages.payment.copyFailed}
                    </div>
                  ) : null}
                </div>

                <div className="analysis-payment-warning">
                  <div className="analysis-payment-warning-title">
                    {t.packages.payment.warningTitle}
                  </div>
                  <ul className="analysis-payment-warning-list">
                    {t.packages.payment.warnings.map((warning, wIdx) => (
                      <li key={wIdx}>{warning}</li>
                    ))}
                  </ul>
                </div>

                <div className="analysis-payment-receipt">
                  <p>{t.packages.payment.receiptText}</p>
                  <a
                    href={PAYMENT_SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="analysis-payment-support-link"
                  >
                    {t.packages.payment.contactSupport}
                  </a>
                </div>

                <div className="analysis-payment-bank">
                  <div className="analysis-payment-bank-title">{t.packages.payment.bankTitle}</div>
                  <p>{t.packages.payment.bankText}</p>
                </div>

                <p className="analysis-payment-manual-note">
                  {t.packages.payment.manualActivation}
                </p>
              </div>
            ) : Array.isArray(t.packages?.visiblePlans) && t.packages.visiblePlans.length > 0 ? (
              <div
                className="analysis-packages-content"
                style={{
                  padding: "4px 2px 8px",
                  overflowY: "auto",
                  color: "rgba(230, 245, 238, 0.86)"
                }}
              >
                {typeof t.packages.intro === "string" && t.packages.intro ? (
                  <p style={{ margin: "0 0 16px", lineHeight: 1.6, color: "rgba(230, 245, 238, 0.86)" }}>
                    {t.packages.intro}
                  </p>
                ) : null}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px"
                  }}
                >
                  {t.packages.visiblePlans
                    .filter(
                      (planKey) =>
                        planKey === "basic" ||
                        planKey === "pro" ||
                        planKey === "premium"
                    )
                    .map((planKey) => {
                      const plan = t.packages[planKey];
                      if (!plan || typeof plan !== "object") return null;
                      const isPremium = planKey === "premium";

                      return (
                        <div
                          key={planKey}
                          style={{
                            position: "relative",
                            padding: "16px 16px 18px",
                            borderRadius: "14px",
                            border: isPremium
                              ? "1px solid rgba(89, 217, 142, 0.45)"
                              : "1px solid rgba(255,255,255,0.12)",
                            background: isPremium
                              ? "rgba(89, 217, 142, 0.08)"
                              : "rgba(255,255,255,0.03)"
                          }}
                        >
                          {plan.badge ? (
                            <span
                              style={{
                                position: "absolute",
                                top: "-10px",
                                right: "16px",
                                padding: "3px 10px",
                                borderRadius: "999px",
                                background: "#59d98e",
                                color: "#0b1f16",
                                fontSize: "12px",
                                fontWeight: 800
                              }}
                            >
                              {plan.badge}
                            </span>
                          ) : null}

                          <div style={{ fontSize: "18px", fontWeight: 800, color: "rgba(230, 245, 238, 0.96)" }}>
                            {plan.name}
                          </div>

                          <div style={{ marginTop: "4px", display: "flex", alignItems: "baseline", gap: "6px" }}>
                            <span style={{ fontSize: "22px", fontWeight: 800, color: "#59d98e" }}>
                              {plan.price}
                            </span>
                            <span style={{ fontSize: "13px", color: "rgba(230, 245, 238, 0.65)" }}>
                              {plan.billingCycle}
                            </span>
                          </div>

                          {typeof plan.tagline === "string" && plan.tagline ? (
                            <p style={{ margin: "10px 0 0", lineHeight: 1.6 }}>{plan.tagline}</p>
                          ) : null}

                          {typeof plan.benefitsTitle === "string" && plan.benefitsTitle ? (
                            <div style={{ marginTop: "12px", fontWeight: 700, color: "rgba(230, 245, 238, 0.92)" }}>
                              {plan.benefitsTitle}
                            </div>
                          ) : null}

                          {Array.isArray(plan.benefits) && plan.benefits.length > 0 ? (
                            <ul style={{ margin: "6px 0 0", paddingLeft: "20px" }}>
                              {plan.benefits.map((b, bIdx) => (
                                <li key={bIdx} style={{ lineHeight: 1.6 }}>
                                  {b}
                                </li>
                              ))}
                            </ul>
                          ) : null}

                          {planKey === "pro" &&
                          (plan.dataTitle || plan.dataDescription || plan.suitableFor) ? (
                            <div
                              style={{
                                marginTop: "12px",
                                padding: "10px 12px",
                                borderRadius: "10px",
                                border: "1px solid rgba(255,255,255,0.1)",
                                background: "rgba(255,255,255,0.03)"
                              }}
                            >
                              {typeof plan.dataTitle === "string" && plan.dataTitle ? (
                                <div style={{ fontWeight: 700, marginBottom: "4px", color: "rgba(230, 245, 238, 0.92)" }}>
                                  {plan.dataTitle}
                                </div>
                              ) : null}
                              {typeof plan.dataDescription === "string" && plan.dataDescription ? (
                                <p style={{ margin: "0 0 8px", lineHeight: 1.6 }}>{plan.dataDescription}</p>
                              ) : null}
                              {typeof plan.suitableFor === "string" && plan.suitableFor ? (
                                <p style={{ margin: 0, lineHeight: 1.6, fontStyle: "italic", color: "rgba(230, 245, 238, 0.7)" }}>
                                  {plan.suitableFor}
                                </p>
                              ) : null}
                            </div>
                          ) : null}

                          {lang === "vi-VN" &&
                          (planKey === "pro" || planKey === "premium") &&
                          typeof plan.paymentButton === "string" ? (
                            <button
                              type="button"
                              className={`analysis-plan-payment-button ${
                                planKey === "premium"
                                  ? "analysis-plan-payment-button--premium"
                                  : ""
                              }`}
                              onClick={() => openPayment(planKey)}
                            >
                              {plan.paymentButton}
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                </div>

                {t.packages.promotion && typeof t.packages.promotion === "object" ? (
                  <div
                    style={{
                      marginTop: "16px",
                      padding: "12px 14px",
                      borderRadius: "12px",
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.03)"
                    }}
                  >
                    {typeof t.packages.promotion.title === "string" && t.packages.promotion.title ? (
                      <div style={{ fontWeight: 800, marginBottom: "6px", color: "rgba(230, 245, 238, 0.96)" }}>
                        {t.packages.promotion.title}
                      </div>
                    ) : null}
                    {typeof t.packages.promotion.description === "string" && t.packages.promotion.description ? (
                      <p style={{ margin: "0 0 8px", lineHeight: 1.6 }}>{t.packages.promotion.description}</p>
                    ) : null}
                    {Array.isArray(t.packages.promotion.benefits) && t.packages.promotion.benefits.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {t.packages.promotion.benefits.map((b, bIdx) => (
                          <li key={bIdx} style={{ lineHeight: 1.6 }}>
                            {b}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : null}

                {typeof t.packages.registerLine === "string" && t.packages.registerLine ? (
                  <p style={{ margin: "16px 0 0", lineHeight: 1.6, fontWeight: 600, color: "rgba(230, 245, 238, 0.92)" }}>
                    {t.packages.registerLine}
                  </p>
                ) : null}

                {typeof t.packages.riskText === "string" && t.packages.riskText ? (
                  <p style={{ margin: "10px 0 0", lineHeight: 1.6, color: "rgba(200, 210, 205, 0.75)" }}>
                    {t.packages.riskText}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="analysis-packages-coming-soon">
                <div className="analysis-packages-coming-soon-title">
                  Package information will be updated soon.
                </div>
              </div>
            )}
          </div>
        )}

        {activeSection === "features" && (
          <div
            className="analysis-login-card analysis-login-panel"
            role="region"
            aria-label="Features"
          >
            <div className="analysis-login-panel-header">
              <div className="analysis-login-panel-title">{t.features.title}</div>
              <button
                type="button"
                className="analysis-login-panel-close"
                onClick={() => setActiveSection(null)}
              >
                {t.common.close}
              </button>
            </div>

            <div className="analysis-login-features">
              {typeof t.features.intro === "string" && t.features.intro ? (
                <p style={{ margin: "0 0 12px", lineHeight: 1.6, color: "rgba(230, 245, 238, 0.86)" }}>
                  {t.features.intro}
                </p>
              ) : null}

              <ul className="analysis-login-features-list">
                {t.features.items.map((item, idx) => (
                  <li key={idx} className="analysis-login-features-item">
                    <div className="analysis-login-features-name">{item.name}</div>
                    <div className="analysis-login-features-desc">{item.description}</div>

                    {Array.isArray(item.sections) && item.sections.length > 0 ? (
                      <details
                        style={{
                          marginTop: "10px",
                          padding: "10px 12px",
                          border: "1px solid rgba(255,255,255,0.12)",
                          borderRadius: "10px",
                          background: "rgba(255,255,255,0.03)"
                        }}
                      >
                        <summary
                          style={{
                            cursor: "pointer",
                            color: "#59d98e",
                            fontWeight: 700
                          }}
                        >
                          {t.features.detailLabel || "Xem chi tiết"}
                        </summary>

                        <div style={{ marginTop: "10px" }}>
                          {item.sections.map((section, sIdx) => (
                            <div key={sIdx} style={{ marginTop: sIdx === 0 ? 0 : "14px" }}>
                              {typeof section.title === "string" ? (
                                <div style={{ fontWeight: 700, marginBottom: "6px", color: "rgba(230, 245, 238, 0.92)" }}>
                                  {section.title}
                                </div>
                              ) : null}

                              {Array.isArray(section.paragraphs)
                                ? section.paragraphs.map((p, pIdx) => (
                                    <p
                                      key={pIdx}
                                      style={{ margin: "0 0 8px", lineHeight: 1.6, color: "rgba(230, 245, 238, 0.86)" }}
                                    >
                                      {p}
                                    </p>
                                  ))
                                : null}

                              {Array.isArray(section.bullets) ? (
                                <ul style={{ margin: "0 0 8px", paddingLeft: "20px" }}>
                                  {section.bullets.map((b, bIdx) => (
                                    <li
                                      key={bIdx}
                                      style={{ lineHeight: 1.6, color: "rgba(230, 245, 238, 0.86)" }}
                                    >
                                      {b}
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                            </div>
                          ))}

                          {typeof item.note === "string" && item.note ? (
                            <p
                              style={{
                                margin: "10px 0 0",
                                lineHeight: 1.6,
                                fontStyle: "italic",
                                color: "rgba(230, 245, 238, 0.7)"
                              }}
                            >
                              {item.note}
                            </p>
                          ) : null}
                        </div>
                      </details>
                    ) : null}
                  </li>
                ))}
              </ul>

              {typeof t.features.usageTitle === "string" &&
              t.features.usageTitle &&
              Array.isArray(t.features.usageBullets) &&
              t.features.usageBullets.length > 0 ? (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "10px 12px",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "10px",
                    background: "rgba(255,255,255,0.03)"
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: "6px", color: "rgba(230, 245, 238, 0.92)" }}>
                    {t.features.usageTitle}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {t.features.usageBullets.map((b, bIdx) => (
                      <li key={bIdx} style={{ lineHeight: 1.6, color: "rgba(230, 245, 238, 0.86)" }}>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {typeof t.features.riskTitle === "string" &&
              t.features.riskTitle &&
              typeof t.features.riskText === "string" &&
              t.features.riskText ? (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "10px 12px",
                    border: "1px solid rgba(255,200,120,0.25)",
                    borderRadius: "10px",
                    background: "rgba(255,200,120,0.06)"
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: "6px", color: "rgba(255,220,170,0.92)" }}>
                    {t.features.riskTitle}
                  </div>
                  <p style={{ margin: 0, lineHeight: 1.6, color: "rgba(255,220,170,0.86)" }}>
                    {t.features.riskText}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {activeSection === "support" && (
          <div
            className="analysis-login-card analysis-login-panel"
            role="region"
            aria-label={t.support.title}
          >
            <div className="analysis-login-panel-header">
              <div className="analysis-login-panel-title">{t.support.title}</div>
              <button
                type="button"
                className="analysis-login-panel-close"
                onClick={() => setActiveSection(null)}
              >
                {t.common.close}
              </button>
            </div>

            <p className="analysis-support-intro">{t.support.intro}</p>

            <p className="analysis-support-contact-hint">
              {t.support.contactHint}
            </p>

            <div className="analysis-support-methods">
              <div className="analysis-support-method">
                <div className="analysis-support-method-copy">
                  <div className="analysis-support-method-label">{t.support.agentLabel}</div>
                  <div className="analysis-support-method-description">{t.support.agentDescription}</div>
                </div>
                <a
                  href="https://t.me/tickter"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="analysis-support-action analysis-support-telegram-link"
                >
                  @tickter
                </a>
              </div>

              <div className="analysis-support-method">
                <div className="analysis-support-method-copy">
                  <div className="analysis-support-method-label">{t.support.botLabel}</div>
                  <div className="analysis-support-method-description">{t.support.botDescription}</div>
                </div>
                <a
                  href="https://t.me/tkb_sport_support_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="analysis-support-action analysis-support-telegram-link"
                >
                  @tkb_sport_support_bot
                </a>
              </div>
            </div>
          </div>
        )}

        {activeSection === "login" && (
          <div
            className="analysis-login-card analysis-login-panel analysis-login-panel--login"
            role="region"
            aria-label="Sign in"
          >
            <div className="analysis-login-panel-header analysis-login-panel-header--login">
              <button
                type="button"
                className="analysis-login-panel-close"
                onClick={() => setActiveSection(null)}
              >
                {t.common.close}
              </button>
            </div>

            <div className="analysis-login-brand">
              <div className="analysis-login-title">{t.login.title}</div>
              <div className="analysis-login-subtitle">{t.login.subtitle}</div>
            </div>

            {kickedReason === "other_device" && (
              <div
                className="analysis-login-error"
                role="alert"
                style={{ marginBottom: "10px", background: "rgba(255,120,30,0.12)", borderColor: "rgba(255,120,30,0.35)" }}
              >
                Your account was signed in on another device.
              </div>
            )}
            {kickedReason === "idle_timeout" && (
              <div
                className="analysis-login-error"
                role="alert"
                style={{ marginBottom: "10px", background: "rgba(255,180,30,0.10)", borderColor: "rgba(255,180,30,0.30)" }}
              >
                You were logged out after 20 minutes of inactivity.
              </div>
            )}
            {kickedReason === "session_invalid" && (
              <div
                className="analysis-login-error"
                role="alert"
                style={{ marginBottom: "10px" }}
              >
                Your session expired. Please sign in again.
              </div>
            )}

            {authMode === "login" ? (
              <>
                <form onSubmit={onSubmit} className="analysis-login-form">
                  <input
                    className="analysis-login-input"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    placeholder="Email"
                    autoComplete="username"
                  />

                  <div className="analysis-login-password-field">
                    <input
                      className="analysis-login-input"
                      type={showLoginPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="analysis-login-password-toggle"
                      aria-pressed={showLoginPassword}
                      aria-label={
                        showLoginPassword
                          ? (lang === "vi-VN" ? "Ẩn mật khẩu" : "Hide password")
                          : (lang === "vi-VN" ? "Hiện mật khẩu" : "Show password")
                      }
                      title={
                        showLoginPassword
                          ? (lang === "vi-VN" ? "Ẩn mật khẩu" : "Hide password")
                          : (lang === "vi-VN" ? "Hiện mật khẩu" : "Show password")
                      }
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setShowLoginPassword((v) => !v)}
                    >
                      {showLoginPassword ? <AnalysisLoginEyeOffIcon /> : <AnalysisLoginEyeIcon />}
                    </button>
                  </div>

                  {err && (
                    <div className="analysis-login-error" role="alert">
                      {err}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="analysis-login-button"
                  >
                    {loading ? "Logging in..." : "Login"}
                  </button>
                </form>

                <button
                  type="button"
                  className="analysis-login-panel-close"
                  style={{ marginTop: "14px" }}
                  disabled={loading}
                  onClick={() => {
                    setErr("");
                    setAuthMode("register");
                  }}
                >
                  {lang === "vi-VN"
                    ? "Chưa có tài khoản? Đăng ký dùng thử 1 ngày"
                    : "Don't have an account? Sign up for a 1-day trial"}
                </button>
              </>
            ) : (
              <AnalysisSelfRegistrationForm
                auth={auth}
                lang={lang}
                onRegistrationComplete={handleSelfRegistrationComplete}
                onBackToLogin={() => {
                  setErr("");
                  setAuthMode("login");
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
